import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';

import { GroupDetailInterface, UserGroupInterface } from 'types/user';
import { ModalContext } from 'context/ModalContext';
import Box from 'components/ui/box';
import { useAuth } from 'context/AuthContext';
import { getUserGroup, getUserGroupList } from 'services/dashboard.service';

import { IoIosClose } from 'react-icons/io';
import Button from 'components/ui/button';

type Props = {
  id?: string;
  player?: string;
  returnTotal?: (value: number) => void;
  cycleLabel: string;
};

const useFocusTrap = (ref: React.RefObject<HTMLDivElement>, isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !ref.current) return;
      event.preventDefault();

      const focusableElements = ref.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusableElements || focusableElements.length === 0) return;

      const focusableArray = Array.from(focusableElements) as HTMLElement[];
      const currentIndex = focusableArray.indexOf(document.activeElement as HTMLElement);

      let nextIndex: number;
      if (event.shiftKey) {
        nextIndex = currentIndex <= 0 ? focusableArray.length - 1 : currentIndex - 1;
      } else {
        nextIndex = currentIndex >= focusableArray.length - 1 ? 0 : currentIndex + 1;
      }

      focusableArray[nextIndex]?.focus();
    };

    const timeoutId = setTimeout(() => {
      if (ref.current) {
        const firstFocusable = ref.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement;
        firstFocusable?.focus();
      }
    }, 50);

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, ref, onClose]);
};

export function GroupDetailModalExtrato({ player, returnTotal, cycleLabel }: Props) {
  const { user } = useAuth();
  const { openModal, closeModal } = useContext(ModalContext);

  const modalID = 'group-detail-extrato';

  const [modalIsOpen, setIsOpen] = useState<boolean>(false);
  const [data, setData] = useState<any>([]);
  const modalContentRef = useRef<HTMLDivElement>(null);

  ReactModal.setAppElement('#modal');

  function handleCloseModal() {
    //
  }

  function afterOpenModal() {
    //
  }

  const handleFocusTrapClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  useFocusTrap(modalContentRef, modalIsOpen, handleFocusTrapClose);

  useEffect(() => {
    setIsOpen(openModal === modalID);
  }, [openModal]);

  useEffect(() => {
    async function loadUserGroup() {
      if (user?._id || player) {
        const data = await getUserGroupList(player, cycleLabel);
        setData(data);

        if (returnTotal) {
          returnTotal(data?.length);
        }
      }
    }

    if (!user || !cycleLabel || !modalIsOpen) return;

    loadUserGroup();
  }, [user, player, cycleLabel, returnTotal, modalIsOpen]);

  if (!data?.length) return <></>;

  return (
    <>
      <ReactModal
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={true}
        isOpen={modalIsOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={handleCloseModal}
        contentLabel="Example Modal"
        portalClassName="relative z-50"
        overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-80 flex flex-col items-center   w-screen h-screen p-6 pt-[10vh] overflow-y-auto"
        contentElement={() => (
          <div ref={modalContentRef} role="dialog" aria-modal="true" aria-labelledby="group-detail-extrato-modal-title">
            <Box className="w-[90%] lg:w-auto lg:max-w-[800px] ">
              <Button
                color="primary"
                className="absolute -right-5 -top-5  rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center"
                onClick={() => closeModal()}
                aria-label="Fechar modal"
              >
                <IoIosClose color="white" size={40} aria-hidden="true" />
              </Button>

              <div className="flex justify-center text-xl text-yellow">
                <h1 tabIndex={0} id="group-detail-extrato-modal-title">Grupo {data[0]?.grupo}</h1>
              </div>

              <div className="overflow-x-auto">
                <div className="border-electric-blue border rounded-xl w-[700px] lg:w-auto">
                  <div className="flex font-bold">
                    <div tabIndex={0} className="w-36 flex items-center p-2 pl-5">Matrícula </div>
                    <div tabIndex={0} className="w-48 border-electric-blue border-l flex items-center p-2 pl-5">Nome</div>
                  </div>

                  {data.map((group: any) => (
                    <div className={`flex  border-electric-blue border-t font-thin`} key={group._id}>
                      <div tabIndex={0} className="nome-dependencia flex items-center justify-center w-36 p-2">{group.chave} </div>
                      <div tabIndex={0} className="prefixo border-electric-blue border-l flex  items-center w-48 p-2">{group.nome}</div>
                    </div>
                  ))}
                </div>
              </div>

              <p tabIndex={0} className="mt-5 font-thin">
                Total de funcionários do grupo: <span className="text-yellow">{data?.length} pessoas.</span>
              </p>
            </Box>
          </div>
        )}
      />
    </>
  );
}
